
import { createElement } from 'react'
import type { RouteObject } from 'react-router-dom'
import type { createStore } from 'jotai'
import { AppContainer } from './AppContainer'
import { pageLinks } from './pageLinks'
import { RouteErrorBoundary } from './components/RouteErrorBoundary'
import NotFoundPage from './pages/NotFoundPage'
import { createAuthRoutes } from './auth'
import LabelGenerator from './pages/function/LabelGenerator'
import * as LabelGeneratorStore from './stores/LabelGeneratorStore'
import Dashboard from './pages/function/Dashboard'
import * as DashboardStore from './stores/DashboardStore'
import AplusDashboard from './pages/function/AplusDashboard'
import AplusListings from './pages/function/AplusListings'
import AplusAddListing from './pages/function/AplusAddListing'
import AplusListingDetail from './pages/function/AplusListingDetail'
import AmazonListings from './pages/function/AmazonListings'
import OrdersDashboard from './pages/function/OrdersDashboard'
import CoPilot from './pages/function/CoPilot'


type Store = ReturnType<typeof createStore>

// ============================================================================
// ⚠️  CRITICAL: pageLinks ↔ createBusinessRoutes Contract
// ============================================================================
// 1. Define ALL page paths in `pageLinks.ts`
// 2. Use `pageLinks.xxx()` in `createBusinessRoutes` below (NOT hardcoded strings)
//
// This ensures:
// - Type-safe navigation throughout the app via `import { pageLinks } from './pageLinks'`
// - Single source of truth for all routes
// - Compile-time errors if routes are missing or mistyped
// ============================================================================

// Auth is handled by backend API middlewares:
// - loginRequired  -> 401 -> redirect to login
// - adminRequired  -> 403 -> Access Denied page
// - publicAccessible / publicWithOptionalAuth -> no auth check
//
function createBusinessRoutes(store: Store): RouteObject[] {
  return [
    // Home: Label Generator (new label, no auth required)
    {
      path: pageLinks.Home(),
      element: createElement(LabelGenerator),
      loader: async () => {
        LabelGeneratorStore.loader(store)
        return null
      },
    },
    // Dashboard: saved labels (auth required — backend returns 401, error boundary redirects to login)
    {
      path: pageLinks.Dashboard(),
      element: createElement(Dashboard),
      loader: async () => {
        DashboardStore.loader(store)
        return null
      },
    },
    // Edit existing label (auth required — backend returns 401)
    {
      path: pageLinks.LabelGenerator(':id'),
      element: createElement(LabelGenerator),
      loader: async ({ params }) => {
        LabelGeneratorStore.loader(store, params)
        return null
      },
    },
    // A+ Content Tracker
    {
      path: pageLinks.AplusDashboard(),
      element: createElement(AplusDashboard),
    },
    {
      path: pageLinks.AplusListings(),
      element: createElement(AplusListings),
    },
    {
      path: pageLinks.AplusAddListing(),
      element: createElement(AplusAddListing),
    },
    {
      path: pageLinks.AplusListingDetail(':id'),
      element: createElement(AplusListingDetail),
    },
    // Amazon Seller
    {
      path: pageLinks.AmazonListings(),
      element: createElement(AmazonListings),
    },
    {
      path: pageLinks.AmazonOrders(),
      element: createElement(OrdersDashboard),
    },
    // CoPilot
    {
      path: pageLinks.CoPilot(),
      element: createElement(CoPilot),
    },
  ]
}

// Fallback routes (must be last in children array)
function createFallbackRoutes(): RouteObject[] {
  const fallbackRoutes: RouteObject[] = []
  // 404 catch-all
  fallbackRoutes.push({
    path: '*',
    element: createElement(NotFoundPage),
  })
  return fallbackRoutes
}

export function createRoutes(store: Store): RouteObject[] {
  return [
    {
      path: '/',
      element: createElement(AppContainer),
      // Route-level error boundary handles AuthRequiredError, ForbiddenError, and other errors
      // This catches errors from loaders/actions that React Router intercepts before global ErrorBoundary
      errorElement: createElement(RouteErrorBoundary),


      children: [
        ...createBusinessRoutes(store),
        ...createAuthRoutes(store),

        ...createFallbackRoutes(),
      ],
    },
  ]
}
